import React, { useState } from "react";
import "./styles.scss";
import { useSnackbar } from "notistack";
import { Box, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import EditIcon from "../../assets/images/expert/edit.png";
import generateSignedUrl, { acceptedFileTypes, getMimeTypeFromExtension, maxSizeBytes } from "./helper";
import { useBoolean } from "../../utils/hooks/useBoolean";
import BackdropComponent from "../../atoms/backdropComponent";

type UploadTypes = {
  setUrl?: (url: string) => void;
  setLoading?: (loading: boolean) => void;
  setController?: any;
}

type GetFile = {
  getFile?: (file: File | null) => void;
}

type Props = {
  dropzoneConfig?: {
    maxSize?: number;
    text?: React.ReactNode;
  };
  fileAcceptedTypes?: string[];
  errorFileAcceptedMsg?: string;
} & UploadTypes & GetFile;

export function FileUpload({ setLoading, setUrl, setController, dropzoneConfig, getFile, fileAcceptedTypes, errorFileAcceptedMsg}: Props) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const { enqueueSnackbar } = useSnackbar();
  const acceptedFormats = fileAcceptedTypes || acceptedFileTypes;
  const {value: isUploadLoading, setValue: setUploadLoading} = useBoolean();


  const onDrop = (acceptedFiles: any) => {

    if (
      acceptedFormats.find((accept) => accept === acceptedFiles[0].type)
    ) {
      if (acceptedFiles[0].size < maxSizeBytes(dropzoneConfig?.maxSize || 10)) {
        setErrorMsg("");
        setSelectedFile(acceptedFiles[0]);
        if (getFile) {
          getFile(acceptedFiles[0]);
        } else {
          handleUpload(acceptedFiles[0], setSelectedFile);
        }
      } else {
        setErrorMsg("Max File Size: " + (dropzoneConfig?.maxSize || 10) + " MB");
      }
    } else {
      setSelectedFile(null);
      setErrorMsg(errorFileAcceptedMsg || "Accepted Formats - .pdf .jpeg .png .jpg .webp");
    }
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: acceptedFormats?.reduce(
      (acc, item) => ({ ...acc, [getMimeTypeFromExtension(item)]: [] }),
      {}
    )
  });

  const handleUpload = async (file: File, setSelectedFile: any) => {
    if (setUrl && setLoading && setController) {
      setUploadLoading(true);
      try {
        if (!file) return;

        setLoading(true);
        const newController = new AbortController();
        setController(() => ({
          control: newController,
          for: "file-upload",
          setSelectedFile,
        }));

        const id = 20;
        const time = new Date().getTime();
        const filename = `${id}-${time}-${file.name}`;
        const signedUrl = await generateSignedUrl(filename);

      const response = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        signal: newController.signal,
        headers: {
          "Content-Type": file.type,
          'x-amz-acl': 'public-read',
        },
      });

        if (response.ok) {
          setUrl(signedUrl.split("?")[0]);
        } else {
          console.error("Error uploading file");
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          enqueueSnackbar("Upload canceled", { variant: "error" });
        } else {
          console.error({ err });
          enqueueSnackbar("Request failed", { variant: "error" });
        }
      } finally {
        setLoading(false);
        setController(() => ({ control: null, for: "" }));
        setUploadLoading(false);
      }
    }
  };

  return (
    <Box className="file-upload-container">
      <BackdropComponent
        isBackdrop={isUploadLoading}
      />
      <Box className="fileBox">
        <div {...getRootProps()} className="dropzone-box">
          <input {...getInputProps()} />
          <>
            {isDragActive ? (
              <p>Drop the File here ...</p>
            ) : !selectedFile ? (
              <>{dropzoneConfig?.text || <p>Drag 'n' drop file here, or click to select file</p>}</>
            ) : (
              <Box className="dropbox-name">
                <Typography>{selectedFile.name}</Typography>
                <img alt="Edit icon" src={EditIcon} />
              </Box>
            )}
          </>
        </div>
      </Box>
      <Box className="error-msg">{errorMsg && <p>{errorMsg}</p>}</Box>
    </Box>
  );
}
